import React, { useState } from 'react';
import './pagestyles/CustomerSupport.css';

const CustomerSupport = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [searchQuery, setSearchQuery] = useState('');
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'technical',
    priority: 'medium',
    description: ''
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setSupportForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Support request submitted:', supportForm);
    alert('Your support request has been submitted. We will respond within 24 hours.');
    // Reset form
    setSupportForm({
      name: '',
      email: '',
      subject: '',
      category: 'technical',
      priority: 'medium',
      description: ''
    });
  };

  const faqCategories = {
    general: [
      {
        question: 'What is Redbox Global Terminal?',
        answer: 'Redbox Global Terminal is a comprehensive financial platform providing real-time market data, news, analytics, and trading capabilities for professionals worldwide.'
      },
      {
        question: 'How do I access my account?',
        answer: 'You can access your account through the Redbox Global Anywhere Remote Login link at the top of the page. Use your credentials to log in from any device.'
      },
      {
        question: 'What are your support hours?',
        answer: 'Our customer support team is available 24/7 to assist you. You can reach us by phone, email, or through the support form below.'
      }
    ],
    technical: [
      {
        question: 'System requirements for Redbox Terminal',
        answer: 'Minimum requirements: Windows 10/macOS 10.15 or later, 8GB RAM, 20GB storage, and a stable internet connection with at least 10 Mbps.'
      },
      {
        question: 'How do I update my terminal software?',
        answer: 'Updates are automatically downloaded and installed. You will receive a notification when an update is available. You can also check for updates manually in Settings > About.'
      },
      {
        question: 'Troubleshooting connection issues',
        answer: 'If you experience connection issues, first check your internet connection. Then verify that your firewall allows Redbox Terminal connections on ports 443 and 8080.'
      }
    ],
    billing: [
      {
        question: 'How is billing structured?',
        answer: 'Billing is based on your subscription tier and usage. Enterprise clients receive customized pricing based on the number of users and features required.'
      },
      {
        question: 'Can I upgrade or downgrade my plan?',
        answer: 'Yes, you can change your subscription plan at any time. Changes will be reflected in your next billing cycle.'
      },
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept major credit cards, wire transfers, and ACH payments for enterprise accounts.'
      }
    ],
    account: [
      {
        question: 'How do I reset my password?',
        answer: 'Click on "Forgot Password" on the login page. Enter your email address, and we will send you instructions to reset your password.'
      },
      {
        question: 'Can I have multiple users on one account?',
        answer: 'Yes, enterprise accounts support multiple users. Each user will have their own login credentials and can have different permission levels.'
      },
      {
        question: 'How do I cancel my subscription?',
        answer: 'To cancel your subscription, please contact your account manager or submit a support ticket. We require 30 days notice for cancellations.'
      }
    ]
  };

  const contactMethods = [
    {
      title: 'Phone Support',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="1.5">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
        </svg>
      ),
      details: [
        'Americas: +1 (212) 318-2000',
        'EMEA: +44 20 7330 7500',
        'APAC: +65 6212 1000',
        'Available 24/7'
      ]
    },
    {
      title: 'Email Support',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="1.5">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
      ),
      details: [
        'support@redboxglobal.com',
        'technical@redboxglobal.com',
        'Response within 24 hours'
      ]
    },
    {
      title: 'Live Chat',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="1.5">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      ),
      details: [
        'Available 24/7',
        'Average response: 2 minutes',
        'Click chat icon to start'
      ]
    },
    {
      title: 'Help Center',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="1.5">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
        </svg>
      ),
      details: [
        'Comprehensive documentation',
        'Video tutorials',
        'User guides & FAQs'
      ]
    }
  ];

  return (
    <div className="page-container">
      <div className="container">
        <div className="support-page">
          <div className="support-header">
            <h1>Customer Support</h1>
            <p className="support-subtitle">
              We're here to help. Get answers to your questions or reach out to our support team.
            </p>
          </div>

          <div className="contact-methods-grid">
            {contactMethods.map((method, index) => (
              <div key={index} className="contact-method-card">
                <div className="contact-icon">{method.icon}</div>
                <h3>{method.title}</h3>
                <ul>
                  {method.details.map((detail, idx) => (
                    <li key={idx}>{detail}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="faq-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-categories">
              <button
                className={activeCategory === 'general' ? 'active' : ''}
                onClick={() => setActiveCategory('general')}
              >
                General
              </button>
              <button
                className={activeCategory === 'technical' ? 'active' : ''}
                onClick={() => setActiveCategory('technical')}
              >
                Technical
              </button>
              <button
                className={activeCategory === 'billing' ? 'active' : ''}
                onClick={() => setActiveCategory('billing')}
              >
                Billing
              </button>
              <button
                className={activeCategory === 'account' ? 'active' : ''}
                onClick={() => setActiveCategory('account')}
              >
                Account
              </button>
            </div>

            <div className="faq-list">
              {faqCategories[activeCategory].map((faq, index) => (
                <div key={index} className="faq-item">
                  <h4>{faq.question}</h4>
                  <p>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="support-form-section">
            <h2>Submit a Support Request</h2>
            <p>Can't find what you're looking for? Fill out the form below and our team will get back to you.</p>
            
            <form className="support-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={supportForm.name}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={supportForm.email}
                    onChange={handleFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={supportForm.subject}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={supportForm.category}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="technical">Technical Support</option>
                    <option value="billing">Billing Question</option>
                    <option value="account">Account Management</option>
                    <option value="feature">Feature Request</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="priority">Priority *</label>
                  <select
                    id="priority"
                    name="priority"
                    value={supportForm.priority}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={supportForm.description}
                  onChange={handleFormChange}
                  rows="6"
                  placeholder="Please provide as much detail as possible..."
                  required
                />
              </div>

              <button type="submit" className="submit-btn">
                Submit Request
              </button>
            </form>
          </div>

          <div className="emergency-contact">
            <div className="emergency-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h3>Emergency Support</h3>
            <p>
              For critical issues requiring immediate attention, please call our emergency hotline:
              <br />
              <strong>+1 (212) 318-2999</strong> (Available 24/7)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSupport;
